/**
 * xr_perf — what the headset's frames cost, readable IN the headset (XR P0.1, 2026-09-28).
 *
 * A Quest has no devtools; Meta's workflow is "measure first" (OVR Metrics Tool: frame time against the budget, CPU
 * vs GPU bound). This is the in-app half: the XR performance module (AtlasVROptimizerModule) feeds one frame at a
 * time with `recordXrFrame`, and the VR panel's Perf page reads `xrPerfSnapshot()`. Pure — no three, no DOM.
 *
 * Budgets (Meta WebXR perf guidance): 72 Hz = 13.9 ms a frame, ~8–9 ms of it for rendering; ~100 draw calls is
 * comfortable, 200 tight, 400 stalls; ~150–300 k visible triangles is a workable standalone scene.
 */

export interface XRPerfSnapshot {
  /** Frames recorded in the rolling window. */
  frames: number;
  /** Median and 95th-percentile frame interval, ms (wall clock between XR frames). */
  p50Ms: number;
  p95Ms: number;
  /** Frames over the target budget, share of the window (0..1). */
  missShare: number;
  /** The session's target rate and its budget. */
  targetHz: number;
  budgetMs: number;
  /** The last frame's draw calls and triangles (three's info.render, per frame). */
  draws: number;
  triangles: number;
  /** The governor's current quality level (0 = best … see xr_headsets.VR_LEVELS), foveation and scale in force. */
  level: number;
  foveation: number;
  framebufferScale: number;
  /** The headset the profile was chosen for (xr_headsets.detectHeadset). */
  headset: string;
}

export const XR_PERF_WINDOW = 180; // ~2.5 s at 72 Hz

const ring = new Float64Array(XR_PERF_WINDOW);
let n = 0;
let head = 0;
const snap: XRPerfSnapshot = {
  frames: 0, p50Ms: 0, p95Ms: 0, missShare: 0, targetHz: 72, budgetMs: 1000 / 72,
  draws: 0, triangles: 0, level: 0, foveation: 0, framebufferScale: 1, headset: 'unknown',
};
let dirty = true;
const scratch: number[] = [];

/** Record one XR frame's interval (ms) and its render counters. Non-finite or absurd intervals (a stall of a
 *  backgrounded session, a clock step) are dropped rather than skewing the window. */
export function recordXrFrame(intervalMs: number, draws: number, triangles: number): void {
  if (Number.isFinite(intervalMs) && intervalMs > 0 && intervalMs < 1000) {
    ring[head] = intervalMs; head = (head + 1) % XR_PERF_WINDOW; if (n < XR_PERF_WINDOW) n++;
    dirty = true;
  }
  if (Number.isFinite(draws)) snap.draws = draws;
  if (Number.isFinite(triangles)) snap.triangles = triangles;
}

/** The session-level facts the snapshot reports (set by the XR performance module). */
export function setXrPerfContext(ctx: Partial<Pick<XRPerfSnapshot, 'targetHz' | 'level' | 'foveation' | 'framebufferScale' | 'headset'>>): void {
  if (ctx.targetHz !== undefined && ctx.targetHz > 0) { snap.targetHz = ctx.targetHz; snap.budgetMs = 1000 / ctx.targetHz; dirty = true; }
  if (ctx.level !== undefined) snap.level = ctx.level;
  if (ctx.foveation !== undefined) snap.foveation = ctx.foveation;
  if (ctx.framebufferScale !== undefined) snap.framebufferScale = ctx.framebufferScale;
  if (ctx.headset !== undefined) snap.headset = ctx.headset;
}

/** Forget the window (a new session, a level change the governor wants to judge afresh). */
export function resetXrPerf(): void { n = 0; head = 0; dirty = true; }

/** The rolling statistics (the same object each call; copy it to keep it). */
export function xrPerfSnapshot(): XRPerfSnapshot {
  if (dirty) {
    scratch.length = 0;
    for (let i = 0; i < n; i++) scratch.push(ring[i]);
    scratch.sort((a, b) => a - b);
    const q = (p: number): number => (scratch.length ? scratch[Math.min(scratch.length - 1, Math.floor(p * scratch.length))] : 0);
    snap.frames = n;
    snap.p50Ms = q(0.5);
    snap.p95Ms = q(0.95);
    // A frame "misses" when it took more than 1.5 budgets (the compositor re-showed the last one).
    let miss = 0;
    for (const v of scratch) if (v > snap.budgetMs * 1.5) miss++;
    snap.missShare = n ? miss / n : 0;
    dirty = false;
  }
  return snap;
}
